import Icon1 from "@assets/icons/Icon";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DropDownItem } from "./DropDownMultiple";


type DropDownProps = {
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  options: DropDownItem[];
  value?: string | number;
  onSelect: (item: string | number) => void;
  className?: string;
  mandatory?: boolean;
  grow_on_focus?: boolean;
  category?: string;
  variant?: "small" | "large";
};

export default function DropDown({
  disabled = false,
  label,
  placeholder,
  options,
  category,
  value = "",
  onSelect,
  className,
  mandatory = false,
  grow_on_focus = false,
  variant = "large",
}: DropDownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [openUpwards, setOpenUpwards] = useState<boolean | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLUListElement>(null); // Add this ref

  const handleSelect = (item: any) => {
    onSelect(item.value);
    setIsOpen(false);
  };

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const shouldOpenUpwards = spaceAbove > spaceBelow && spaceBelow < 200;
      setOpenUpwards(shouldOpenUpwards);

      setDropdownPosition({
        top: shouldOpenUpwards ? rect.top - 8 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside the trigger button
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }

      // Check if click is inside the portal dropdown
      if (portalRef.current && portalRef.current.contains(target)) {
        return;
      }

      // Click is outside both - close the dropdown
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  let has_value = value !== "" && value !== null && value !== undefined;
  let disabled_classname =
    " focus:outline-none focus:ring-transparent focus:border-neutral-300 focus:border-1 text-shadow_25 bg-neutral-100 border-neutral-300";

  // Portal dropdown content
  const dropdownContent = isOpen ? (
    <ul
      ref={portalRef} // Add the ref here
      style={{
        position: "fixed",
        top: openUpwards
          ? dropdownPosition.top - 95
          : dropdownPosition.top - 70,
        left: dropdownPosition.left,
        maxWidth: dropdownPosition.width,
        zIndex: 9999,
      }}
      className="flex flex-col w-full min-w-200 bg-white shadow-lg rounded-xl p-1 border   sm:text-sm"
    >
      <div className="text-neutral-400 text-h3 px-2 py-1 ">
        {category || label}
      </div>{" "}
      <div className=" overflow-y-auto max-h-200">
        {options
          .filter((item) => item?.value !== undefined)
          .map((item, index) => (
            <li
              key={index}
              className={`cursor-pointer select-none relative rounded-lg py-2 pl-2 pr-2 ${
                has_value && value === item.value
                  ? "text-black font-semibold"
                  : "text-gray-900 hover:bg-neutral-100"
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(item);
              }}
            >
              <div className="flex options-center">
                <span
                  className={`block text-h2  ${
                    has_value && value === item.value ? "font-regular" : ""
                  }`}
                >
                  {item.label}
                  {item.extra_label && (
                    <span
                      className={`${
                        item.type
                          ? item.type === "particulier"
                            ? "text-purple-400 bg-purple-50"
                            : item.type === "constructor"
                              ? "text-neutral-400 bg-neutral-50"
                              : item.type === "expert"
                                ? "text-green-400 bg-green-50"
                                : "text-blue-400 bg-blue-50"
                          : "text-blue-400 bg-blue-50"
                      } font-light rounded-full px-1 text-h3 ml-1`}
                    >
                      {item.extra_label}
                    </span>
                  )}
                </span>
              </div>
              {has_value && value === item.value ? (
                <span className="absolute inset-y-0 right-0 flex options-center pr-4 py-2">
                  <svg
                    className="h-5 w-5 text-neutral-500"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 5.707 8.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              ) : null}
            </li>
          ))}
        {(options || []).length === 0 && (
          <li className="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-500">
            Aucune option disponible
          </li>
        )}
      </div>
    </ul>
  ) : null;

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="text-h4 text-neutral-400 ">{label}</div>{" "}
      <div
        className={`relative select-none outline-none border-0 ${className}`}
        style={{ width: grow_on_focus ? (isOpen ? "" : "150px") : "" }}
      >
        <div
          ref={dropdownRef}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          tabIndex={0}
          className={`
            ${variant === "small" ? " py-2" : " py-3"}
            
            relative w-full px-3 outline-none h-fit bg-white border flex items-center flex-row justify-between rounded-xl shadow-sm ${
              disabled
                ? disabled_classname
                : mandatory && !has_value && !isOpen
                  ? "border-red-300 cursor-pointer border-2 outline-1 outline-red-100"
                  : " cursor-pointer bg-transparent border"
            } sm:text-sm`}
        >
          {mandatory && !has_value && !isOpen && (
            <div className="absolute top-0 justify-start h-full options-start flex py-0 flex-row right-8 text-white text-h6">
              <div className="bg-red-300 flex flex-row options-end rounded-b-lg px-1 h-5 pb-0.5">
                Champ obligatoire
              </div>
            </div>
          )}

          {!isOpen && (
            <div className="absolute top-1/3 right-2 px-1">
              <Icon1
                name="down"
                size={15}
                color={mandatory && !has_value && !isOpen ? "red" : "grey"}
              />
            </div>
          )}
          {isOpen && (
            <div className="absolute top-1/3 right-3">
              <Icon1 name="up" size={15} color="grey" />
            </div>
          )}
          <div className="flex flex-row options-center justify-between w-full mr-4 text-wrap">
            {has_value && (
              <div className="flex flex-row  justify-between options-center w-full">
                <span>
                  {
                    options
                      .filter((item) => item?.value !== undefined)
                      .find((item) => item.value === value)?.label
                  }
                </span>
              </div>
            )}
            {!has_value && (
              <div className="flex flex-row justify-between options-center w-full">
                <div className="text-neutral-400">{placeholder || label || "\u00A0"}</div>
              </div>
            )}
          </div>
        </div>

        {typeof window !== "undefined" &&
          createPortal(dropdownContent, document.body)}
      </div>
    </div>
  );
}
